import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message
} from 'antd';
import {
  CopyOutlined,
  PlusOutlined,
  QrcodeOutlined,
  StopOutlined
} from '@ant-design/icons';
import { QRCode } from 'react-qrcode-logo';
import { classJoinQrcodeAPI } from '../../services/api';

const { Paragraph, Text } = Typography;

const JOIN_TYPE_CONFIG = {
  teacher: { color: 'blue', label: '教师入班码' },
  parent: { color: 'green', label: '家长入班码' }
};

const STATUS_CONFIG = {
  active: { color: 'success', label: '有效' },
  disabled: { color: 'default', label: '已停用' },
  expired: { color: 'warning', label: '已过期' }
};

const ClassQRCodeManagement = ({ classId, classInfo }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [codes, setCodes] = useState([]);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [currentCode, setCurrentCode] = useState(null);
  const [form] = Form.useForm();

  const fetchCodes = async () => {
    if (!classId) {
      return;
    }

    try {
      setLoading(true);
      const response = await classJoinQrcodeAPI.getByClass(classId);
      if (response.data.success) {
        setCodes(response.data.data || []);
      } else {
        message.error(response.data.message || '获取班级二维码失败');
      }
    } catch (error) {
      console.error('获取班级二维码失败:', error);
      message.error(error.response?.data?.message || '获取班级二维码失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCodes();
  }, [classId]);

  const handleCopy = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      message.success('二维码内容已复制');
    } catch (error) {
      console.error('复制二维码内容失败:', error);
      message.error('复制失败，请手动复制');
    }
  };

  const handleCreate = async (values) => {
    try {
      setSaving(true);
      const response = await classJoinQrcodeAPI.create({
        class_id: classId,
        join_type: values.join_type,
        max_uses: values.max_uses,
        expiry_days: values.expiry_days,
        notes: values.notes
      });

      if (response.data.success) {
        message.success(response.data.message || '班级二维码生成成功');
        setCreateModalVisible(false);
        form.resetFields();
        fetchCodes();
      } else {
        message.error(response.data.message || '班级二维码生成失败');
      }
    } catch (error) {
      console.error('生成班级二维码失败:', error);
      message.error(error.response?.data?.message || '班级二维码生成失败');
    } finally {
      setSaving(false);
    }
  };

  const handleDisable = async (id) => {
    try {
      const response = await classJoinQrcodeAPI.disable(id);
      if (response.data.success) {
        message.success('二维码已停用');
        fetchCodes();
      } else {
        message.error(response.data.message || '停用二维码失败');
      }
    } catch (error) {
      console.error('停用二维码失败:', error);
      message.error(error.response?.data?.message || '停用二维码失败');
    }
  };

  const columns = [
    {
      title: '二维码类型',
      dataIndex: 'join_type',
      key: 'join_type',
      width: 140,
      render: (value) => {
        const config = JOIN_TYPE_CONFIG[value] || { color: 'default', label: value };
        return <Tag color={config.color}>{config.label}</Tag>;
      }
    },
    {
      title: '二维码内容',
      dataIndex: 'code',
      key: 'code',
      width: 180,
      render: (value) => (
        <Space>
          <Text code strong>{value}</Text>
          <Button type="text" size="small" icon={<CopyOutlined />} onClick={() => handleCopy(value)} />
        </Space>
      )
    },
    {
      title: '使用次数',
      key: 'usage',
      width: 120,
      render: (_, record) => `${record.used_count} / ${record.max_uses > 0 ? record.max_uses : '不限'}`
    },
    {
      title: '有效期',
      dataIndex: 'expires_at',
      key: 'expires_at',
      width: 180,
      render: (value) => (value ? new Date(value).toLocaleString() : '长期有效')
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (value) => {
        const config = STATUS_CONFIG[value] || { color: 'default', label: value };
        return <Tag color={config.color}>{config.label}</Tag>;
      }
    },
    {
      title: '备注',
      dataIndex: 'notes',
      key: 'notes',
      ellipsis: true,
      render: (value) => value || <Text type="secondary">-</Text>
    },
    {
      title: '操作',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<QrcodeOutlined />}
            onClick={() => {
              setCurrentCode(record);
              setQrModalVisible(true);
            }}
          >
            查看码
          </Button>
          {record.status === 'active' ? (
            <Popconfirm
              title="确认停用这个二维码吗？"
              onConfirm={() => handleDisable(record.id)}
              okText="确认"
              cancelText="取消"
            >
              <Button type="link" size="small" danger icon={<StopOutlined />}>
                停用
              </Button>
            </Popconfirm>
          ) : null}
        </Space>
      )
    }
  ];

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      <Card>
        <Alert
          type="info"
          showIcon
          message={`当前班级：${classInfo?.name || '-'}${classInfo?.grade ? ` / ${classInfo.grade}` : ''}`}
          description="教师入班码用于老师扫码后加入该班级；家长入班码用于家长扫码后加入该班级。学校二维码仍在“学校邀请码”页面统一管理。"
        />
      </Card>

      <Card
        title="班级二维码管理"
        extra={(
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalVisible(true)}>
            新增二维码
          </Button>
        )}
      >
        <Paragraph type="secondary" style={{ marginTop: -4 }}>
          建议教师入班码用于班级协作老师加入，家长入班码用于家长先加入班级，再完成学生绑定。
        </Paragraph>
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={codes}
          pagination={{ pageSize: 10, showSizeChanger: true }}
          scroll={{ x: 900 }}
        />
      </Card>

      <Modal
        title="新增班级二维码"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={560}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            join_type: 'teacher',
            max_uses: 20,
            expiry_days: 30
          }}
          onFinish={handleCreate}
        >
          <Form.Item
            name="join_type"
            label="二维码类型"
            rules={[{ required: true, message: '请选择二维码类型' }]}
          >
            <Select
              options={[
                { value: 'teacher', label: '教师入班码' },
                { value: 'parent', label: '家长入班码' }
              ]}
            />
          </Form.Item>

          <Form.Item
            name="max_uses"
            label="最大使用次数"
            rules={[{ required: true, message: '请输入最大使用次数' }]}
          >
            <InputNumber min={1} max={9999} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="expiry_days"
            label="有效期（天）"
            rules={[{ required: true, message: '请输入有效期' }]}
          >
            <InputNumber min={1} max={3650} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="notes" label="备注">
            <Input.TextArea rows={3} placeholder="例如：2026春季家长入班码" maxLength={200} showCount />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <Button type="primary" htmlType="submit" loading={saving}>
                生成二维码
              </Button>
              <Button onClick={() => setCreateModalVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="班级二维码"
        open={qrModalVisible}
        onCancel={() => setQrModalVisible(false)}
        footer={(
          <Button onClick={() => setQrModalVisible(false)}>
            关闭
          </Button>
        )}
        width={420}
      >
        {currentCode ? (
          <div style={{ textAlign: 'center' }}>
            <Tag color={JOIN_TYPE_CONFIG[currentCode.join_type]?.color || 'default'} style={{ marginBottom: 12 }}>
              {JOIN_TYPE_CONFIG[currentCode.join_type]?.label || currentCode.join_type}
            </Tag>
            <QRCode value={currentCode.code} size={240} level="H" includeMargin />
            <div style={{ marginTop: 16 }}>
              <Text strong style={{ fontSize: 22 }}>{currentCode.code}</Text>
            </div>
            <div style={{ marginTop: 12 }}>
              <Button type="primary" icon={<CopyOutlined />} onClick={() => handleCopy(currentCode.code)}>
                复制二维码内容
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>
    </Space>
  );
};

export default ClassQRCodeManagement;
